import React from 'react';
import { motion } from 'framer-motion';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CTA } from '../components/CTA';
import { UKFlag } from '../components/UKFlag';
import { FlaskConical, ShieldCheck, Truck, Microscope } from 'lucide-react';
const PILLARS = [{
  icon: FlaskConical,
  title: 'Lab verification, not lab marketing.',
  body: 'Every batch goes to an ISO 17025-accredited UK laboratory for independent HPLC + mass-spec analysis. The chromatogram for your exact batch is downloadable on the product page — not a stock COA from last year.'
}, {
  icon: Microscope,
  title: 'Sourced from a single GMP facility.',
  body: 'Our peptides come from one EU-based facility that synthesises and lyophilises in-house under documented procedures. No re-packers, no anonymous re-sellers. We can show the exact lot the powder came from.'
}, {
  icon: Truck,
  title: 'Cold-chain dispatch from the UK.',
  body: 'Tracked, temperature-controlled, discreet packaging — out the door from our Manchester facility before 2pm every weekday. International orders ship from the same address with full export documentation.'
}, {
  icon: ShieldCheck,
  title: 'A 30-day researcher guarantee.',
  body: 'If a batch ever reads outside its declared purity tolerance, or arrives compromised — we re-ship, replace, or refund. No back-and-forth. It is the researcher\u2019s job to question results; it is our job to remove that question.'
}];
const TIMELINE = [{
  year: '2022',
  label: 'Founded in Manchester by two ex-pharma analytical chemists.'
}, {
  year: '2023',
  label: 'First independent HPLC partnership signed with an ISO-accredited UK lab.'
}, {
  year: '2024',
  label: 'Cold-chain UK dispatch network completed; same-day cut-off introduced.'
}, {
  year: '2025',
  label: 'Expanded to 18 verified peptides; serving 1,200+ UK research customers.'
}, {
  year: '2026',
  label: 'Every batch chromatogram now public on product pages.'
}];
export function About() {
  return <div className="min-h-screen w-full bg-background" data-id="element-845">
      <AnnouncementBar data-id="element-846" />
      <Header data-id="element-847" />

      <main data-id="element-848">
        {/* Hero */}
        <section className="container mx-auto pt-14 lg:pt-20 pb-16 lg:pb-20" data-id="element-849">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-850">
            <motion.div initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="lg:col-span-7" data-id="element-851">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card mb-7" data-id="element-852">
                <UKFlag size={14} data-id="element-853" />
                <span className="text-[11px] font-medium tracking-wide uppercase text-muted-foreground" data-id="element-854">
                  Made &amp; tested in the UK
                </span>
              </div>
              <h1 className="font-semibold tracking-[-0.035em] text-foreground text-[38px] sm:text-[52px] lg:text-[64px] leading-[1.04]" data-id="element-855">
                A peptide supplier that
                <br data-id="element-856" />
                <span className="text-foreground" data-id="element-857">
                  treats researchers like
                </span>{' '}
                <span className="text-primary" data-id="element-858">researchers</span>
                <span className="text-foreground" data-id="element-859">.</span>
              </h1>
              <p className="mt-6 max-w-[600px] text-[17px] leading-[1.55] text-muted-foreground" data-id="element-860">
                We sell research-grade peptides to UK and European labs,
                universities, and independent researchers. Every batch is
                independently verified. Every chromatogram is published. Every
                question gets a real answer from someone with a lab coat in the
                cupboard behind them.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3" data-id="element-861">
                <CTA to="/shop" variant="primary" size="lg" data-id="element-862">
                  Browse peptides
                </CTA>
                <CTA to="/faq" variant="secondary" size="lg" data-id="element-863">
                  Read the FAQ
                </CTA>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="lg:col-span-5" data-id="element-864">
              <div className="rounded-3xl bg-secondary/40 border border-border p-8 lg:p-10" data-id="element-865">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-5" data-id="element-866">
                  By the numbers
                </div>
                <ul className="space-y-5" data-id="element-867">
                  {[{
                  stat: '99.8%',
                  label: 'Average HPLC purity across catalogue'
                }, {
                  stat: '1,247',
                  label: 'Verified Trustpilot reviews · 4.8★'
                }, {
                  stat: '100%',
                  label: 'Batches tested by an ISO 17025 lab'
                }, {
                  stat: '< 24h',
                  label: 'UK dispatch on orders before 2pm'
                }].map(s => <li key={s.label} className="flex items-baseline justify-between gap-4 pb-5 border-b border-border last:border-0 last:pb-0" data-id="element-868">
                      <span className="text-[26px] font-semibold tracking-[-0.02em] text-foreground leading-none [font-feature-settings:'tnum']" data-id="element-869">
                        {s.stat}
                      </span>
                      <span className="text-[12.5px] text-muted-foreground text-right max-w-[180px] leading-snug" data-id="element-870">
                        {s.label}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pillars */}
        <section className="w-full bg-card border-y border-border" data-id="element-871">
          <div className="container mx-auto py-20 lg:py-28" data-id="element-872">
            <div className="max-w-2xl mb-14" data-id="element-873">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-874">
                What we stand for
              </p>
              <h2 className="text-3xl lg:text-5xl font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-875">
                Four commitments, written down
                <span className="text-primary" data-id="element-876">.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-12" data-id="element-877">
              {PILLARS.map(({
              icon: Icon,
              title,
              body
            }, i) => <motion.div key={title} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.45,
              delay: i * 0.05
            }} className="flex gap-5" data-id="element-878">
                  <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-879">
                    <Icon className="h-[18px] w-[18px] text-primary" strokeWidth={1.75} data-id="element-880" />
                  </div>
                  <div data-id="element-881">
                    <h3 className="text-[18px] font-semibold tracking-tight text-foreground leading-snug" data-id="element-882">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground" data-id="element-883">
                      {body}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="container mx-auto py-20 lg:py-28" data-id="element-884">
          <div className="grid lg:grid-cols-[1fr_1.8fr] gap-10 lg:gap-20" data-id="element-885">
            <div className="lg:sticky lg:top-24 lg:self-start" data-id="element-886">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-887">
                Our story
              </p>
              <h2 className="text-3xl lg:text-[44px] font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-888">
                Four years of one quiet question
                <span className="text-primary" data-id="element-889">.</span>
              </h2>
              <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground max-w-md" data-id="element-890">
                &quot;Why can&apos;t a researcher just see the chromatogram for
                the exact vial they bought?&quot; We started peptides.ml to
                answer that question, and the rest is a list of small
                infrastructure decisions that compound.
              </p>
            </div>

            <ol className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden" data-id="element-891">
              {TIMELINE.map((t, i) => <li key={t.year} className="flex items-start gap-6 px-6 lg:px-8 py-6 lg:py-7" data-id="element-892">
                  <span className="font-mono text-[13px] text-muted-foreground font-medium w-12 flex-shrink-0 mt-0.5 [font-feature-settings:'tnum']" data-id="element-893">
                    {t.year}
                  </span>
                  <span className="text-[15px] text-foreground leading-relaxed" data-id="element-894">
                    {t.label}
                  </span>
                </li>)}
            </ol>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-foreground text-background border-t border-border" data-id="element-895">
          <div className="container mx-auto py-16 lg:py-20 grid lg:grid-cols-2 items-center gap-10" data-id="element-896">
            <div data-id="element-897">
              <p className="text-[11px] uppercase tracking-wider text-background/60 font-medium mb-3" data-id="element-898">
                Start researching
              </p>
              <h2 className="text-3xl lg:text-5xl font-semibold tracking-[-0.025em] leading-[1.05]" data-id="element-899">
                Pick a peptide. We&apos;ll handle the proof
                <span className="text-primary" data-id="element-900">.</span>
              </h2>
            </div>
            <div className="flex flex-wrap justify-end items-center gap-3" data-id="element-901">
              <CTA to="/shop" variant="primary" size="lg" data-id="element-902">
                Browse peptides
              </CTA>
              <CTA to="/faq" variant="ghost" size="lg" className="!text-background !bg-transparent border border-background/20 hover:!bg-background/10" data-id="element-903">
                Read the FAQ
              </CTA>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-904" />
    </div>;
}